import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import WarningIcon from "../Icons/WarningIcon";
import useEscapeKey from "@hooks/useEscapeKey";

const DuplicateWarningModal = ({
  isOpen,
  onClose,
  onGoToComponent,
  importComponentSeq,
  title = "",
  description = "",
  loading = false,
  "data-testid": dataTestId = "duplicate-warning-modal",
}) => {
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { colors } = useTheme();
  const { t } = useTranslation();

  useEscapeKey(onClose, isOpen && !loading);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleGoToComponent = () => {
    if (!loading && importComponentSeq && onGoToComponent) {
      onGoToComponent(importComponentSeq);
    }
  };

  const handleCancel = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <div
      data-testid={`${dataTestId}-overlay`}
      className={`fixed inset-0 z-50 flex items-center justify-start sm:justify-center p-4 ${colors.bgBlurModal} backdrop-blur-[1px] overflow-x-auto`}
    >
      <div
        data-testid={dataTestId}
        className={`relative w-full max-w-md min-w-md ${colors.bgModal} transform overflow-hidden rounded-xl shadow-xl transition-all duration-200`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Content */}
        <div className="p-6" data-testid={`${dataTestId}-content`}>
          {/* Icon and Title */}
          <div className="mb-4 flex items-center justify-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center">
              <WarningIcon size={56} active />
            </div>
          </div>

          {/* Title */}
          <div className="mb-4 text-center">
            <h3 className={`text-lg font-semibold ${colors.textPrimary} mb-2 uppercase`} data-testid={`${dataTestId}-title`}>
              {title}
            </h3>

            {/* Description */}
            <p className={`text-sm ${colors.textSecondary} whitespace-pre-line`} data-testid={fieldValueTestId("description")}>
              {description}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex flex-row gap-3 justify-center" data-testid={`${dataTestId}-footer`}>
            {/* Cancel Button */}
            <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={handleCancel} disabled={loading}>
              {t("common.cancel-button")}
            </OutlineButton>

            {/* Go To Component Button */}
            <PrimaryButton data-testid={`${dataTestId}-confirm-button`} onClick={handleGoToComponent} disabled={loading || !importComponentSeq}>
              {t("common.yes")}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateWarningModal;
